// Country code to currency code mapping
export const COUNTRY_CODE_TO_CURRENCY_MAP: { [key: string]: string } = {
  "IND": "INR",
  "USA": "USD",
  "GBR": "GBP",
  "EUR": "EUR",
  "JPN": "JPY",
  "CAN": "CAD",
  "AUS": "AUD",
  "CHN": "CNY",
  "SGP": "SGD",
  "ARE": "AED",
  "SAU": "SAR",
  "KWT": "KWD",
  "BHR": "BHD",
  "OMN": "OMR",
  "QAT": "QAR",
  "JOR": "JOD",
  "EGY": "EGP",
  "ZAF": "ZAR",
  "NGA": "NGN",
  "KEN": "KES",
  "THA": "THB",
  "MYS": "MYR",
  "IDN": "IDR",
  "PHL": "PHP",
  "VNM": "VND",
  "BGD": "BDT",
  "PAK": "PKR",
  "LKA": "LKR",
  "NPL": "NPR",
};

// Currency code to country code mapping (reverse of above)
export const CURRENCY_TO_COUNTRY_CODE_MAP: { [key: string]: string } = Object.fromEntries(
  Object.entries(COUNTRY_CODE_TO_CURRENCY_MAP).map(([country, currency]) => [currency, country])
);

// Utility function to get currency symbol
export const getCurrencySymbol = (currency: string): string => {
  const currencyMap: { [key: string]: string } = {
    'INR': '₹',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'CHF',
    'CNY': '¥',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'CZK': 'Kč',
    'HUF': 'Ft',
    'RUB': '₽',
    'BRL': 'R$',
    'MXN': '$',
    'KRW': '₩',
    'SGD': 'S$',
    'HKD': 'HK$',
    'NZD': 'NZ$',
    'ZAR': 'R',
    'TRY': '₺',
    'ILS': '₪',
    'AED': 'د.إ',
    'SAR': '﷼',
    'QAR': '﷼',
    'KWD': 'د.ك',
    'BHD': 'د.ب',
    'OMR': '﷼',
    'JOD': 'د.ا',
    'LBP': 'ل.ل',
    'EGP': '£',
    'MAD': 'د.م.',
    'TND': 'د.ت',
    'DZD': 'د.ج',
    'LYD': 'ل.د',
    'ETB': 'Br',
    'KES': 'KSh',
    'UGX': 'USh',
    'TZS': 'TSh',
    'GHS': '₵',
    'NGN': '₦',
    'XOF': 'CFA',
    'XAF': 'FCFA',
    'MUR': '₨',
    'SCR': '₨',
    'LKR': '₨',
    'PKR': '₨',
    'BDT': '৳',
    'NPR': '₨',
    'BTN': 'Nu.',
    'MMK': 'K',
    'LAK': '₭',
    'KHR': '៛',
    'VND': '₫',
    'THB': '฿',
    'MYR': 'RM',
    'IDR': 'Rp',
    'PHP': '₱',
    'TWD': 'NT$',
    'MOP': 'MOP$',
    'BND': 'B$',
    'FJD': 'FJ$',
    'PGK': 'K',
    'SBD': 'SI$',
    'TOP': 'T$',
    'WST': 'WS$',
    'VUV': 'Vt',
    'XPF': '₣',
    'NIO': 'C$',
    'GTQ': 'Q',
    'HNL': 'L',
    'SVC': '$',
    'BZD': 'BZ$',
    'JMD': 'J$',
    'TTD': 'TT$',
    'BBD': 'Bds$',
    'XCD': 'EC$',
    'AWG': 'ƒ',
    'ANG': 'ƒ',
    'SRD': '$',
    'GYD': 'G$',
    'BWP': 'P',
    'SZL': 'L',
    'LSL': 'L',
    'ZMW': 'ZK',
    'MWK': 'MK',
    'BIF': 'FBu',
    'RWF': 'RF',
    'DJF': 'Fdj',
    'SOS': 'S',
    'ERN': 'Nfk',
    'KMF': 'CF',
    'MGA': 'Ar',
    'MVR': 'Rf',
    'AFN': '؋',
    'AMD': '֏',
    'AZN': '₼',
    'GEL': '₾',
    'KZT': '₸',
    'KGS': 'лв',
    'TJS': 'SM',
    'TMT': 'T',
    'UZS': 'лв',
    'MNT': '₮',
    'KPW': '₩'
  };
  
  return currencyMap[currency.toUpperCase()] || currency;
};

// Utility function to format currency with symbol
export const formatCurrency = (amount: number, currency: string = 'INR'): string => {
  const symbol = getCurrencySymbol(currency);
  return `${symbol} ${amount.toLocaleString()}`;
};

// Utility function to get last 7 days date range
export const getLast7DaysRange = (): string => {
  const today = new Date();
  const lastWeek = new Date();
  lastWeek.setDate(today.getDate() - 7);
  
  const formatDate = (date: Date, includeYear: boolean = false): string => {
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return includeYear ? `${day} ${month} ${year}` : `${day} ${month}`;
  };
  
  const startDate = formatDate(lastWeek, false); // No year for start date
  const endDate = formatDate(today, false); // No year for end date
  const year = today.getFullYear(); // Get year from today
  
  return `${startDate} to ${endDate} ${year}`;
};

// Utility function to get last 1 month date range
export const getLastMonthRange = (): string => {
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);
  
  const formatDate = (date: Date, includeYear: boolean = false): string => {
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return includeYear ? `${day} ${month} ${year}` : `${day} ${month}`;
  };
  
  const startDate = formatDate(lastMonth, false); // No year for start date
  const endDate = formatDate(today, false); // No year for end date
  const year = today.getFullYear(); // Get year from today
  
  return `${startDate} to ${endDate} ${year}`;
};